import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, BarChart2, Clock, CreditCard, FileText,
  RefreshCw, TrendingUp, Users,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts'
import { useCustomerKpis, type CustomerOrder, type CustomerInvoice, type CustomerContract } from '../hooks/useCustomers'

const MONTH_LABELS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']

function fmt(n: number) {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)
}

function KpiTile({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div className="km-panel p-5">
      <p className="text-xs font-semibold uppercase tracking-wider text-[--km-text-muted] mb-2">{label}</p>
      <p className={clsx('text-2xl font-bold tabular-nums', color ?? 'text-[--km-text-primary]')}>{value}</p>
      {sub && <p className="text-xs text-[--km-text-muted] mt-1">{sub}</p>}
    </div>
  )
}

function SectionTitle({ icon: Icon, title }: { icon: React.ElementType; title: string }) {
  return (
    <div className="flex items-center gap-2.5 mb-4">
      <div className="h-7 w-7 rounded-lg bg-indigo-500/15 flex items-center justify-center">
        <Icon size={14} className="text-indigo-400" />
      </div>
      <h2 className="text-sm font-bold text-[--km-text-primary] uppercase tracking-wider">{title}</h2>
    </div>
  )
}

function OrdersTable({ orders }: { orders: CustomerOrder[] }) {
  if (orders.length === 0) {
    return <p className="text-sm text-[--km-text-muted] py-4">Nessuna commessa attiva.</p>
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.06]">
            {['Data', 'Service Line', 'Stato', 'Valore', 'Costo CTD', 'Ore', 'Margine'].map(h => (
              <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[--km-text-muted]">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/[0.04]">
          {orders.map(o => (
            <tr key={o.order_id} className="hover:bg-white/[0.02] transition-colors">
              <td className="px-4 py-3 text-[--km-text-secondary] tabular-nums text-xs">
                {o.order_date ? o.order_date.slice(0, 10) : '—'}
              </td>
              <td className="px-4 py-3 text-[--km-text-secondary]">{o.service_line ?? '—'}</td>
              <td className="px-4 py-3">
                <span className="rounded-full bg-indigo-500/10 px-2.5 py-0.5 text-xs font-semibold text-indigo-400 ring-1 ring-indigo-500/20">
                  {o.status}
                </span>
              </td>
              <td className="px-4 py-3 tabular-nums text-[--km-text-primary] font-semibold">{fmt(o.order_value)}</td>
              <td className="px-4 py-3 tabular-nums text-[--km-text-secondary]">{fmt(o.cost_to_date)}</td>
              <td className="px-4 py-3 tabular-nums text-[--km-text-secondary]">{o.hours_worked.toFixed(0)}h</td>
              <td className="px-4 py-3 tabular-nums font-semibold">
                <span className={clsx(
                  o.margin_pct >= 25 ? 'text-emerald-400'
                  : o.margin_pct >= 15 ? 'text-amber-400'
                  : 'text-red-400',
                )}>
                  {o.margin_pct.toFixed(1)}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function InvoicesTable({ invoices }: { invoices: CustomerInvoice[] }) {
  if (invoices.length === 0) {
    return <p className="text-sm text-[--km-text-muted] py-4">Nessuna fattura aperta.</p>
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.06]">
            {['Fattura', 'Data emissione', 'Scadenza', 'In sospeso', 'Stato'].map(h => (
              <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[--km-text-muted]">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/[0.04]">
          {invoices.map(inv => (
            <tr key={inv.invoice_id} className={clsx(
              'hover:bg-white/[0.02] transition-colors',
              inv.overdue ? 'bg-red-500/[0.02]' : '',
            )}>
              <td className="px-4 py-3 font-mono text-xs text-[--km-text-muted]">
                {inv.invoice_id.slice(0, 8)}…
              </td>
              <td className="px-4 py-3 text-xs text-[--km-text-secondary] tabular-nums">
                {inv.invoice_date?.slice(0, 10) ?? '—'}
              </td>
              <td className="px-4 py-3 text-xs tabular-nums">
                <span className={inv.overdue ? 'text-red-400 font-semibold' : 'text-[--km-text-secondary]'}>
                  {inv.due_date?.slice(0, 10) ?? '—'}
                </span>
              </td>
              <td className="px-4 py-3 tabular-nums font-semibold">
                <span className={inv.overdue ? 'text-red-400' : 'text-[--km-text-primary]'}>
                  {fmt(inv.outstanding)}
                </span>
              </td>
              <td className="px-4 py-3">
                <span className={clsx(
                  'rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1',
                  inv.overdue
                    ? 'bg-red-500/10 text-red-400 ring-red-500/20'
                    : 'bg-amber-500/10 text-amber-400 ring-amber-500/20',
                )}>
                  {inv.overdue ? 'Scaduta' : inv.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ContractsTable({ contracts }: { contracts: CustomerContract[] }) {
  if (contracts.length === 0) {
    return <p className="text-sm text-[--km-text-muted] py-4">Nessun contratto ricorrente attivo.</p>
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.06]">
            {['Service Line', 'Periodicità', 'Valore', 'Mensile', 'Inizio', 'Fine', 'Rinnovo auto'].map(h => (
              <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[--km-text-muted]">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/[0.04]">
          {contracts.map(c => (
            <tr key={c.contract_id} className="hover:bg-white/[0.02] transition-colors">
              <td className="px-4 py-3 text-[--km-text-secondary]">{c.service_line ?? '—'}</td>
              <td className="px-4 py-3 text-[--km-text-muted] text-xs">{c.recurring_period}m</td>
              <td className="px-4 py-3 tabular-nums text-[--km-text-primary] font-semibold">{fmt(c.recurring_value)}</td>
              <td className="px-4 py-3 tabular-nums text-indigo-400 font-semibold">{fmt(c.monthly_value)}/m</td>
              <td className="px-4 py-3 text-xs text-[--km-text-secondary] tabular-nums">{c.start_date?.slice(0, 10) ?? '—'}</td>
              <td className="px-4 py-3 text-xs text-[--km-text-secondary] tabular-nums">{c.end_date?.slice(0, 10) ?? '∞'}</td>
              <td className="px-4 py-3">
                {c.auto_renewal ? (
                  <span className="rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-semibold text-emerald-400 ring-1 ring-emerald-500/20">
                    Sì
                  </span>
                ) : (
                  <span className="text-xs text-[--km-text-muted]">No</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function CustomerDetail() {
  const { customerName } = useParams<{ customerName: string }>()
  const navigate = useNavigate()
  const name = decodeURIComponent(customerName ?? '')
  const { data: kpis, isLoading, error } = useCustomerKpis(name)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-[--km-text-muted] text-sm">
        Caricamento scheda cliente...
      </div>
    )
  }

  if (error || !kpis) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <p className="text-red-400 text-sm">Impossibile caricare i dati del cliente.</p>
        <button
          onClick={() => navigate('/clienti')}
          className="text-indigo-400 text-sm hover:text-indigo-300 flex items-center gap-1"
        >
          <ArrowLeft size={14} /> Torna alla lista
        </button>
      </div>
    )
  }

  const chartData = kpis.revenue_by_month.map(r => ({
    month: MONTH_LABELS[r.month - 1],
    revenue: r.amount,
  }))

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/clienti')}
          className="flex items-center gap-1.5 text-xs text-[--km-text-muted] hover:text-indigo-400 transition-colors mb-4"
        >
          <ArrowLeft size={13} /> Tutti i clienti
        </button>
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg"
            style={{ boxShadow: '0 0 24px rgba(139,92,246,0.35), 0 4px 16px rgba(0,0,0,0.4)' }}
          >
            <Users size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[--km-text-primary] tracking-tight">{name}</h1>
            <p className="text-sm text-[--km-text-muted] mt-0.5">
              Scheda cliente — dati aggiornati al {new Date(kpis.computed_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}
            </p>
          </div>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        <KpiTile label="Revenue YTD" value={fmt(kpis.revenue_ytd)} />
        <KpiTile
          label="Margine medio"
          value={`${kpis.avg_margin_pct.toFixed(1)}%`}
          color={kpis.avg_margin_pct >= 25 ? 'text-emerald-400' : kpis.avg_margin_pct >= 15 ? 'text-amber-400' : 'text-red-400'}
        />
        <KpiTile label="Ore lavorate" value={`${kpis.total_hours.toFixed(0)}h`} sub="commesse attive" />
        <KpiTile
          label="Crediti aperti"
          value={fmt(kpis.total_receivables)}
          sub={kpis.total_overdue > 0 ? `${fmt(kpis.total_overdue)} scaduti` : undefined}
          color={kpis.total_overdue > 0 ? 'text-red-400' : undefined}
        />
        <KpiTile
          label="DSO medio"
          value={kpis.avg_payment_days !== null ? `${kpis.avg_payment_days}g` : '—'}
          color={
            kpis.avg_payment_days === null ? undefined
            : kpis.avg_payment_days <= 30 ? 'text-emerald-400'
            : kpis.avg_payment_days <= 60 ? 'text-amber-400'
            : 'text-red-400'
          }
        />
        <KpiTile
          label="MRR"
          value={kpis.monthly_recurring > 0 ? fmt(kpis.monthly_recurring) : '—'}
          sub={kpis.active_contracts_count > 0 ? `${kpis.active_contracts_count} contratti` : undefined}
          color="text-indigo-400"
        />
      </div>

      {/* Revenue chart */}
      <div className="km-panel p-6">
        <SectionTitle icon={BarChart2} title="Revenue mensile — anno corrente" />
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fill: 'var(--km-text-muted)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fill: 'var(--km-text-muted)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={62}
                tickFormatter={v => `€${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip
                contentStyle={{ background: 'rgba(12,15,24,0.95)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, fontSize: 12 }}
                formatter={(v: number) => [fmt(v), 'Revenue']}
                cursor={{ fill: 'rgba(255,255,255,0.03)' }}
              />
              <Bar dataKey="revenue" fill="url(#barGrad)" radius={[4, 4, 0, 0]} />
              <defs>
                <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#818cf8" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0.5} />
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Commesse attive */}
      <div className="km-panel p-6">
        <SectionTitle icon={TrendingUp} title={`Commesse attive (${kpis.active_orders_count})`} />
        <OrdersTable orders={kpis.orders} />
      </div>

      {/* Fatture aperte */}
      <div className="km-panel p-6">
        <SectionTitle icon={FileText} title={`Fatture aperte (${kpis.invoices_open.length})`} />
        <InvoicesTable invoices={kpis.invoices_open} />
      </div>

      {/* Contratti ricorrenti */}
      <div className="km-panel p-6">
        <SectionTitle icon={RefreshCw} title={`Contratti ricorrenti (${kpis.active_contracts_count})`} />
        <ContractsTable contracts={kpis.contracts} />
      </div>

      {/* Storico pagamenti */}
      {kpis.payment_history.length > 0 && (
        <div className="km-panel p-6">
          <SectionTitle icon={CreditCard} title="Storico pagamenti (ultime 10 fatture)" />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {['Data fattura', 'Data pagamento', 'Importo', 'Giorni'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[--km-text-muted]">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {kpis.payment_history.map((p, i) => (
                  <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3 text-xs text-[--km-text-secondary] tabular-nums">{p.invoice_date?.slice(0, 10) ?? '—'}</td>
                    <td className="px-4 py-3 text-xs text-[--km-text-secondary] tabular-nums">{p.paid_date?.slice(0, 10) ?? '—'}</td>
                    <td className="px-4 py-3 tabular-nums text-[--km-text-primary] font-semibold">{fmt(p.amount)}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {p.days_to_pay !== null ? (
                        <span className="flex items-center gap-1.5">
                          <Clock size={12} className="text-[--km-text-muted]" />
                          <span className={clsx(
                            'text-xs font-semibold',
                            p.days_to_pay <= 30 ? 'text-emerald-400'
                            : p.days_to_pay <= 60 ? 'text-amber-400'
                            : 'text-red-400',
                          )}>
                            {p.days_to_pay}g
                          </span>
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
